<template>
  <div class="p-4 space-y-4">
    <!-- Encabezado -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100">Ingresos</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Registra y consulta tus ingresos</p>
      </div>
      <button
        @click="abrirModalNuevo"
        class="px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1.5"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Nuevo Ingreso
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cuenta</label>
          <select v-model="filtros.cuenta_bancaria_id" @change="cargarMovimientos" class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option :value="null">Todas las cuentas</option>
            <option v-for="cuenta in cuentas" :key="cuenta.id" :value="cuenta.id">{{ cuenta.nombre }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría</label>
          <select v-model="filtros.categoria_id" @change="cargarMovimientos" class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
            <option :value="null">Todas las categorías</option>
            <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nombre }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Inicio</label>
          <input v-model="filtros.fecha_inicio" @change="cargarMovimientos" type="date" class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha Fin</label>
          <input v-model="filtros.fecha_fin" @change="cargarMovimientos" type="date" class="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
        </div>
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-if="cargando && movimientosIngresos.length === 0" class="text-center py-12">
      <div class="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Cargando ingresos...</p>
    </div>

    <!-- Lista de ingresos -->
    <div v-else-if="movimientosIngresos.length > 0" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Fecha</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Concepto</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoría</th>
              <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cuenta</th>
              <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Monto</th>
              <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="movimiento in movimientosIngresos" :key="movimiento.id" class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <td class="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                {{ formatearFecha(movimiento.fecha) }}
              </td>
              <td class="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ movimiento.concepto }}
              </td>
              <td class="px-4 py-2 text-sm">
                <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium" :style="{ backgroundColor: movimiento.categoria?.color + '20', color: movimiento.categoria?.color }">
                  {{ movimiento.categoria?.nombre }}
                </span>
              </td>
              <td class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                {{ movimiento.cuenta_bancaria?.nombre }}
              </td>
              <td class="px-4 py-2 text-sm text-right font-semibold text-green-600">
                ${{ Number(movimiento.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 }) }}
              </td>
              <td class="px-4 py-2 text-right">
                <button @click="eliminarMovimiento(movimiento.id)" class="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400" title="Eliminar">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-else class="text-center py-12 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
      <svg class="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">No hay ingresos registrados</p>
      <button @click="abrirModalNuevo" class="mt-3 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700">
        Registrar primer ingreso
      </button>
    </div>

    <!-- Modal para Nuevo Ingreso -->
    <div v-if="modalAbierto" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="cerrarModal">
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Registrar Ingreso</h2>
          <button @click="cerrarModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="guardarIngreso" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Concepto *</label>
            <input v-model="formulario.concepto" type="text" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" placeholder="Ej: Venta de producto X" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Categoría *</label>
              <select v-model="formulario.categoria_id" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                <option value="">Selecciona una categoría</option>
                <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nombre }}</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cuenta Bancaria *</label>
              <select v-model="formulario.cuenta_bancaria_id" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                <option value="">Selecciona una cuenta</option>
                <option v-for="cuenta in cuentas" :key="cuenta.id" :value="cuenta.id">{{ cuenta.nombre }}</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monto *</label>
              <input v-model.number="formulario.monto" type="number" step="0.01" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" placeholder="0.00" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha *</label>
              <input v-model="formulario.fecha" type="date" required class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referencia</label>
            <input v-model="formulario.referencia" type="text" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" placeholder="Número de factura, folio, etc." />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notas</label>
            <textarea v-model="formulario.notas" rows="3" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" placeholder="Información adicional..."></textarea>
          </div>

          <div class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" @click="cerrarModal" class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700">
              Cancelar
            </button>
            <button type="submit" :disabled="cargando" class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              <span v-if="cargando" class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Guardar Ingreso
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useMovimientos } from '@/composables/finanzas/useMovimientos'

const {
  movimientosIngresos,
  categorias,
  cuentas,
  cargando,
  filtros,
  cargarMovimientos,
  cargarCategorias,
  cargarCuentas,
  crearMovimiento,
  eliminarMovimiento
} = useMovimientos()

const modalAbierto = ref(false)
const formulario = ref({
  tipo: 'ingreso',
  concepto: '',
  categoria_id: '',
  cuenta_bancaria_id: '',
  monto: 0,
  fecha: new Date().toISOString().split('T')[0],
  referencia: '',
  notas: ''
})

const abrirModalNuevo = () => {
  formulario.value = {
    tipo: 'ingreso',
    concepto: '',
    categoria_id: '',
    cuenta_bancaria_id: '',
    monto: 0,
    fecha: new Date().toISOString().split('T')[0],
    referencia: '',
    notas: ''
  }
  modalAbierto.value = true
}

const cerrarModal = () => {
  modalAbierto.value = false
}

const guardarIngreso = async () => {
  await crearMovimiento(formulario.value)
  cerrarModal()
}

const formatearFecha = (fecha) => {
  return new Date(fecha).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

onMounted(async () => {
  filtros.value.tipo = 'ingreso'
  await Promise.all([
    cargarMovimientos(),
    cargarCategorias('ingreso'),
    cargarCuentas()
  ])
})
</script>
